import React, { useCallback } from 'react';

import { Box } from '@mui/material';
import {
  DataGridPro,
  GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
  GridColDef,
  GridPaginationModel,
  GridRowParams,
  GridSortModel,
} from '@mui/x-data-grid-pro';

import { Code } from '~/components/Common/Code';
import GridPagination from '~/components/Common/Elements/GridPagination';

import { formatDateWithClock } from '~/services/TimeService';

import { Cart } from '~/types/services/carts';

import DetailPanelToggle from './DetailPanelToggle';
import {
  cartCreatedAtToDate,
  cartRowId,
  itemTypesLabel,
  travelEndDate,
  travelStartDate,
  travellerCount,
} from './formatters';

interface Props {
  rows: Array<Cart>;
  total: number;
  page: number;
  pageSize: number;
  sortDir: 'ASC' | 'DESC';
  loading: boolean;
  onPaginationChange: (page: number, pageSize: number) => void;
  onSortChange: (sortDir: 'ASC' | 'DESC') => void;
}

const columns: Array<GridColDef<Cart>> = [
  {
    field: 'transactionKey',
    headerName: 'Cart ID',
    flex: 1.4,
    sortable: false,
    display: 'flex',
  },
  {
    field: 'createdAt',
    headerName: 'Timestamp',
    flex: 1.2,
    sortable: true,
    display: 'flex',
    valueGetter: (_value, row) => cartCreatedAtToDate(row.createdAt),
    renderCell: (params) => (params.value ? formatDateWithClock(params.value) : '—'),
  },
  {
    field: 'travellerCount',
    headerName: 'Traveller Count',
    width: 140,
    sortable: false,
    display: 'flex',
    valueGetter: (_value, row) => travellerCount(row.rawItems),
  },
  {
    field: 'travelStartDate',
    headerName: 'Travel Start Date',
    width: 160,
    sortable: false,
    display: 'flex',
    valueGetter: (_value, row) => travelStartDate(row.rawItems) ?? '—',
  },
  {
    field: 'travelEndDate',
    headerName: 'Travel End Date',
    width: 160,
    sortable: false,
    display: 'flex',
    valueGetter: (_value, row) => travelEndDate(row.rawItems) ?? '—',
  },
  {
    field: 'itemTypes',
    headerName: 'Item Types',
    flex: 1,
    sortable: false,
    display: 'flex',
    valueGetter: (_value, row) => itemTypesLabel(row.rawItems),
  },
  {
    field: 'type',
    headerName: 'Statuses',
    width: 120,
    sortable: false,
    display: 'flex',
    valueGetter: (_value, row) => row.type ?? '—',
  },
  {
    ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
    headerName: 'More Details',
    width: 140,
    renderCell: (params) => <DetailPanelToggle {...params} />,
  },
];

export default function CartsTable({
  rows,
  total,
  page,
  pageSize,
  sortDir,
  loading,
  onPaginationChange,
  onSortChange,
}: Props) {
  const handlePaginationModelChange = useCallback(
    (model: GridPaginationModel) => {
      onPaginationChange(model.page, model.pageSize);
    },
    [onPaginationChange],
  );

  const handleSortModelChange = useCallback(
    (model: GridSortModel) => {
      const createdAtSort = model.find((item) => item.field === 'createdAt');
      const nextDir: 'ASC' | 'DESC' = createdAtSort?.sort === 'asc' ? 'ASC' : 'DESC';
      onSortChange(nextDir);
    },
    [onSortChange],
  );

  const getDetailPanelContent = useCallback(
    ({ row }: GridRowParams<Cart>) => (
      <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
        <Code value={JSON.stringify(row.rawItems, null, 2)} lang="json" />
      </Box>
    ),
    [],
  );

  return (
    <DataGridPro
      rows={rows}
      rowCount={total}
      columns={columns}
      getRowId={cartRowId}
      loading={loading}
      pagination
      paginationMode="server"
      paginationModel={{ page, pageSize }}
      onPaginationModelChange={handlePaginationModelChange}
      pageSizeOptions={[pageSize]}
      sortingMode="server"
      sortingOrder={['asc', 'desc']}
      sortModel={[{ field: 'createdAt', sort: sortDir === 'ASC' ? 'asc' : 'desc' }]}
      onSortModelChange={handleSortModelChange}
      getDetailPanelContent={getDetailPanelContent}
      getDetailPanelHeight={() => 'auto'}
      slots={{ pagination: GridPagination }}
      disableColumnFilter
      disableColumnMenu
      disableRowSelectionOnClick
      autoHeight
      density="compact"
    />
  );
}
